import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, BACKEND_URL } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  ChevronLeft, Loader2, Save, Image, Upload, Trash2, 
  FileText, Home, Info, Phone, Briefcase, Eye, RefreshCw,
  Settings, Shield, Mail
} from 'lucide-react';

// Page configurations
const PAGE_CONFIG = {
  home: {
    name: 'Home Page',
    icon: Home,
    sections: [
      { key: 'hero_title', label: 'Hero Title', type: 'text' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea' },
      { key: 'hero_cta', label: 'Hero Button Text', type: 'text' },
      { key: 'features_title', label: 'Features Section Title', type: 'text' },
      { key: 'feature_1_title', label: 'Feature 1 Title', type: 'text' },
      { key: 'feature_1_desc', label: 'Feature 1 Description', type: 'textarea' },
      { key: 'feature_2_title', label: 'Feature 2 Title', type: 'text' },
      { key: 'feature_2_desc', label: 'Feature 2 Description', type: 'textarea' },
      { key: 'feature_3_title', label: 'Feature 3 Title', type: 'text' },
      { key: 'feature_3_desc', label: 'Feature 3 Description', type: 'textarea' },
    ],
    images: [
      { key: 'hero_image', label: 'Hero Background Image' },
      { key: 'feature_1_image', label: 'Feature 1 Image' },
      { key: 'feature_2_image', label: 'Feature 2 Image' },
      { key: 'feature_3_image', label: 'Feature 3 Image' },
    ]
  },
  about: {
    name: 'About Page',
    icon: Info,
    sections: [
      { key: 'page_title', label: 'Page Title', type: 'text' },
      { key: 'intro_text', label: 'Introduction Text', type: 'textarea' },
      { key: 'mission_title', label: 'Mission Title', type: 'text' },
      { key: 'mission_text', label: 'Mission Text', type: 'textarea' },
      { key: 'team_title', label: 'Team Section Title', type: 'text' },
      { key: 'team_text', label: 'Team Description', type: 'textarea' },
    ],
    images: [
      { key: 'hero_image', label: 'Page Header Image' },
      { key: 'team_image', label: 'Team Photo' },
    ]
  },
  services: {
    name: 'Services Page',
    icon: Briefcase,
    sections: [
      { key: 'page_title', label: 'Page Title', type: 'text' },
      { key: 'intro_text', label: 'Introduction Text', type: 'textarea' },
      { key: 'service_1_title', label: 'Service 1 Title', type: 'text' },
      { key: 'service_1_desc', label: 'Service 1 Description', type: 'textarea' },
      { key: 'service_2_title', label: 'Service 2 Title', type: 'text' },
      { key: 'service_2_desc', label: 'Service 2 Description', type: 'textarea' },
      { key: 'service_3_title', label: 'Service 3 Title', type: 'text' },
      { key: 'service_3_desc', label: 'Service 3 Description', type: 'textarea' },
    ],
    images: [
      { key: 'hero_image', label: 'Page Header Image' },
      { key: 'service_1_image', label: 'Service 1 Image' },
      { key: 'service_2_image', label: 'Service 2 Image' },
      { key: 'service_3_image', label: 'Service 3 Image' },
    ]
  },
  contact: {
    name: 'Contact Page',
    icon: Phone,
    sections: [
      { key: 'page_title', label: 'Page Title', type: 'text' },
      { key: 'intro_text', label: 'Introduction Text', type: 'textarea' },
      { key: 'address', label: 'Address', type: 'textarea' },
      { key: 'phone', label: 'Phone Number', type: 'text' },
      { key: 'email', label: 'Email Address', type: 'text' },
      { key: 'hours', label: 'Business Hours', type: 'textarea' },
    ],
    images: [
      { key: 'hero_image', label: 'Page Header Image' },
    ]
  }
};

const AdminCMS = () => {
  const [activePage, setActivePage] = useState('home');
  const [activeTab, setActiveTab] = useState('pages');
  const [content, setContent] = useState({});
  const [globalSettings, setGlobalSettings] = useState({
    logo_url: '',
    tagline: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    disclaimer: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [selectedImageField, setSelectedImageField] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadPageContent(activePage);
    loadImages();
    loadGlobalSettings();
  }, [activePage]);

  const loadGlobalSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/global`);
      setGlobalSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to load global settings:', error);
    }
  };

  const saveGlobalSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/global`, globalSettings, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Global settings saved!');
    } catch (error) {
      toast.error('Failed to save global settings');
    }
    setSaving(false);
  };

  const loadPageContent = async (page) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/content/${page}`);
      setContent(response.data);
    } catch (error) {
      console.error('Failed to load content:', error);
      setContent({ page, sections: {}, images: {} });
    }
    setLoading(false);
  };

  const loadImages = async () => {
    try {
      const response = await axios.get(`${API}/admin/cms/images`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setImages(response.data.images || []);
    } catch (error) {
      console.error('Failed to load images:', error);
    }
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cms/${activePage}`, content, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Content saved successfully!');
    } catch (error) {
      toast.error('Failed to save content');
    }
    setSaving(false);
  };

  const updateSection = (key, value) => {
    setContent(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [key]: value
      }
    }));
  };

  const updateImage = (key, imageId) => {
    setContent(prev => ({
      ...prev,
      images: {
        ...prev.images,
        [key]: imageId
      }
    }));
    setImagePickerOpen(false);
  };

  const openImagePicker = (field) => {
    setSelectedImageField(field);
    setImagePickerOpen(true);
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/cms/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Image uploaded!');
      loadImages();
      
      // Auto-select the uploaded image
      if (selectedImageField) {
        updateImage(selectedImageField.key, response.data.image_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    }
    setUploading(false);
    event.target.value = '';
  };

  const deleteImage = async (imageId) => {
    if (!confirm('Delete this image?')) return;
    
    try {
      await axios.delete(`${API}/admin/cms/images/${imageId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Image deleted');
      loadImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const getImageUrl = (imageId) => {
    if (!imageId) return null;
    return `${BACKEND_URL}/api/cms/images/${imageId}`;
  };

  const pageConfig = PAGE_CONFIG[activePage];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-white/50 hover:text-white">
                <ChevronLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white">Content Management</h1>
                <p className="text-white/50 text-sm">Edit website content, settings, and disclaimer</p>
              </div>
            </div>
          </div>

          {/* Main Tabs: Pages vs Global Settings */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-[#111] border border-white/10 p-1">
              <TabsTrigger
                value="pages"
                className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black text-white/70 px-6 py-2"
              >
                <FileText className="w-4 h-4 mr-2" />
                Page Content
              </TabsTrigger>
              <TabsTrigger
                value="global"
                className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black text-white/70 px-6 py-2"
              >
                <Settings className="w-4 h-4 mr-2" />
                Global Settings
              </TabsTrigger>
            </TabsList>

            {/* Page Content Tab */}
            <TabsContent value="pages" className="space-y-6">
              <div className="flex justify-end">
                <Button
                  onClick={saveContent}
                  disabled={saving}
                  className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Page Changes
                </Button>
              </div>

              {/* Page Tabs */}
              <Tabs value={activePage} onValueChange={setActivePage} className="space-y-6">
                <TabsList className="bg-[#111] border border-white/10 p-1 h-auto flex-wrap">
                  {Object.entries(PAGE_CONFIG).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <TabsTrigger
                        key={key}
                        value={key}
                        className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/70 px-4 py-2"
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {config.name}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {Object.entries(PAGE_CONFIG).map(([key, config]) => (
                  <TabsContent key={key} value={key} className="space-y-6">
                    {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Text Content */}
                    <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                      <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-[#CCFF00]" />
                        Text Content
                      </h3>
                      
                      <div className="space-y-4">
                        {config.sections.map((section) => (
                          <div key={section.key}>
                            <Label className="text-white/70 text-sm">{section.label}</Label>
                            {section.type === 'textarea' ? (
                              <Textarea
                                value={content.sections?.[section.key] || ''}
                                onChange={(e) => updateSection(section.key, e.target.value)}
                                placeholder={`Enter ${section.label.toLowerCase()}...`}
                                className="bg-white/5 border-white/10 text-white mt-1 min-h-[80px]"
                              />
                            ) : (
                              <Input
                                value={content.sections?.[section.key] || ''}
                                onChange={(e) => updateSection(section.key, e.target.value)}
                                placeholder={`Enter ${section.label.toLowerCase()}...`}
                                className="bg-white/5 border-white/10 text-white mt-1"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Images */}
                    <div className="bg-[#111] border border-white/10 rounded-sm p-6">
                      <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <Image className="w-5 h-5 text-[#CCFF00]" />
                        Images
                      </h3>
                      
                      <div className="space-y-4">
                        {config.images.map((imageField) => {
                          const imageId = content.images?.[imageField.key];
                          const imageUrl = getImageUrl(imageId);
                          
                          return (
                            <div key={imageField.key} className="border border-white/10 rounded-sm p-4">
                              <Label className="text-white/70 text-sm">{imageField.label}</Label>
                              
                              <div className="mt-2">
                                {imageUrl ? (
                                  <div className="relative">
                                    <img 
                                      src={imageUrl} 
                                      alt={imageField.label}
                                      className="w-full h-32 object-cover rounded-sm"
                                    />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() => openImagePicker(imageField)}
                                        className="bg-white/20 hover:bg-white/30"
                                      >
                                        <RefreshCw className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => updateImage(imageField.key, null)}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <Button
                                    variant="outline"
                                    onClick={() => openImagePicker(imageField)}
                                    className="w-full h-32 border-dashed border-white/20 text-white/50 hover:text-white hover:border-[#CCFF00]"
                                  >
                                    <div className="text-center">
                                      <Upload className="w-8 h-8 mx-auto mb-2" />
                                      <span>Select Image</span>
                                    </div>
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>
            ))}
              </Tabs>
            </TabsContent>

            {/* Global Settings Tab */}
            <TabsContent value="global" className="space-y-6">
              <div className="flex justify-end">
                <Button
                  onClick={saveGlobalSettings}
                  disabled={saving}
                  className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Global Settings
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Branding & Contact */}
                <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
                  <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                    <Info className="w-5 h-5 text-[#CCFF00]" />
                    Branding & Contact
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Logo Upload */}
                    <div>
                      <Label className="text-white/70">Company Logo</Label>
                      <div className="mt-2">
                        {globalSettings.logo_url ? (
                          <div className="relative inline-block">
                            <img 
                              src={globalSettings.logo_url} 
                              alt="Company Logo" 
                              className="h-16 md:h-20 w-auto max-w-[250px] object-contain bg-white/10 rounded p-2"
                            />
                            <Button
                              size="sm"
                              variant="destructive"
                              className="absolute -top-2 -right-2 w-6 h-6 p-0 rounded-full"
                              onClick={() => setGlobalSettings(prev => ({ ...prev, logo_url: '' }))}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-white/20 rounded p-4 text-center">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-white/30" />
                            <p className="text-white/50 text-sm">No logo uploaded</p>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            try {
                              const response = await axios.post(`${API}/admin/cms/upload-image`, formData, {
                                headers: { 
                                  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
                                  'Content-Type': 'multipart/form-data'
                                }
                              });
                              const imageUrl = `${BACKEND_URL}/api/cms/images/${response.data.image_id}`;
                              setGlobalSettings(prev => ({ ...prev, logo_url: imageUrl }));
                              toast.success('Logo uploaded!');
                            } catch (error) {
                              toast.error('Failed to upload logo');
                            }
                          }}
                          className="bg-white/5 border-white/10 text-white text-sm"
                        />
                      </div>
                      <p className="text-white/40 text-xs mt-1">Upload PNG, JPG, or SVG (recommended: 200x60px)</p>
                    </div>
                    
                    <div>
                      <Label className="text-white/70">Tagline</Label>
                      <Input
                        value={globalSettings.tagline}
                        onChange={(e) => setGlobalSettings(prev => ({ ...prev, tagline: e.target.value }))}
                        placeholder="Your company tagline"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-white/70">Contact Email</Label>
                      <Input
                        type="email"
                        value={globalSettings.contact_email}
                        onChange={(e) => setGlobalSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                        placeholder="hello@company.com"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-white/70">Contact Phone</Label>
                      <Input
                        value={globalSettings.contact_phone}
                        onChange={(e) => setGlobalSettings(prev => ({ ...prev, contact_phone: e.target.value }))}
                        placeholder="+61 (0) 123 456 789"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-white/70">Address</Label>
                      <Input
                        value={globalSettings.contact_address}
                        onChange={(e) => setGlobalSettings(prev => ({ ...prev, contact_address: e.target.value }))}
                        placeholder="Melbourne, Victoria, Australia"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Disclaimer / Terms */}
                <div className="bg-[#111] border border-white/10 p-6 rounded-sm">
                  <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#CCFF00]" />
                    Order Disclaimer / Terms
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white/70">Disclaimer Text</Label>
                      <Textarea
                        value={globalSettings.disclaimer}
                        onChange={(e) => setGlobalSettings(prev => ({ ...prev, disclaimer: e.target.value }))}
                        placeholder="Enter your order terms and conditions... Supports Markdown formatting."
                        className="bg-white/5 border-white/10 text-white mt-1 min-h-[300px] font-mono text-sm"
                      />
                      <p className="text-white/40 text-xs mt-2">
                        This disclaimer is shown to customers before they place their first order. 
                        Supports Markdown formatting (**bold**, *italic*, numbered lists, etc.)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Image Picker Dialog */}
      <Dialog open={imagePickerOpen} onOpenChange={setImagePickerOpen}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-[#CCFF00]" />
              Select Image for: {selectedImageField?.label}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Upload New */}
            <div className="border-2 border-dashed border-white/20 rounded-sm p-6 text-center">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={uploadImage}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600]"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Upload New Image
              </Button>
              <p className="text-white/50 text-xs mt-2">Max 5MB. JPEG, PNG, GIF, WebP</p>
            </div>

            {/* Existing Images */}
            {images.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-white/70 mb-3">Or select from library:</h4>
                <div className="grid grid-cols-3 gap-3">
                  {images.map((img) => (
                    <div 
                      key={img.image_id}
                      className="relative group cursor-pointer border border-white/10 rounded-sm overflow-hidden hover:border-[#CCFF00]"
                      onClick={() => updateImage(selectedImageField?.key, img.image_id)}
                    >
                      <img 
                        src={getImageUrl(img.image_id)} 
                        alt={img.filename}
                        className="w-full h-24 object-cover"
                      />
                      <div className="absolute inset-0 bg-[#CCFF00]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-black font-bold text-sm bg-[#CCFF00] px-2 py-1 rounded">Select</span>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="absolute top-1 right-1 w-6 h-6 p-0 opacity-0 group-hover:opacity-100"
                        onClick={(e) => { e.stopPropagation(); deleteImage(img.image_id); }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminCMS;
